import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer'; // For scroll animations
import { sendCodedLog } from '../../services/logService'; // Adjust path as needed
import Common from '../../common/Common'; // Adjust path as needed
import { FaPlayCircle } from "react-icons/fa"; // Import play icon

// Placeholder image for video thumbnails
// Replace this with a more suitable default video thumbnail if you have one.
import defaultVideoThumb from '../../assets/images/merkaba/Merkaba.png'; 

// --- Simulated Database Data ---
// This array represents the transmissions a user has access to.
// In a real application, this would come from an actual API call to your backend.
const mockUserTransmissions = [];
const mockUserTransmissions2 = [
  {
    id: 'going-gamma',
    title: "Bashar - Going Gamma (2015-11-14)",
    subtitle: "Explore the concept of 'Going Gamma' and its implications for personal transformation and energetic shifts.",
    url: "https://dn721301.ca.archive.org/0/items/bashar-going-gamma-2015-11-14_202406/Bashar%20-%20Going%20Gamma%20%282015-11-14%29.ia.mp4",
    // thumbnail: 'URL_TO_GAMMA_THUMBNAIL.jpg' // Add actual thumbnail URLs here if available
  },
  {
    id: 'money-wealth-abundance',
    title: "Bashar - Mechanism for Achieving Money, Wealth, and Abundance",
    subtitle: "Understand how abundance works and how you can attract it into your life, from Bashar's perspective.",
    url: "https://archive.org/download/bashar-mechanism-for-achieving-money-wealth-and-abundance/Bashar%20-%20Mechanism%20for%20acheiving%2C%20Wealth%2C%20and%20Abundance.ia.mp4",
    // thumbnail: 'URL_TO_MONEY_THUMBNAIL.jpg'
  },
  {
    id: 'soul-blueprint-workshop',
    title: "Bashar - Soul Blueprint Workshop",
    subtitle: "A deep dive into your inherent design and purpose.",
    url: "https://dn710204.ca.archive.org/0/items/bashar-soul-blueprint-workshop_202406/Bashar%20-%20Soul%20Blueprint%20Workshop.ia.mp4",
    // thumbnail: 'URL_TO_SOUL_THUMBNAIL.jpg'
  },
  {
    id: 'the-formula-2015',
    title: "Bashar - The Formula (2015-11-07)",
    subtitle: "Uncover the universal formula for manifestation and conscious creation as taught by Bashar.",
    url: "https://archive.org/download/bashar-the-formula-2015-11-07/Bashar%20-%20The%20Formula%20%282015-11-07%29.ia.mp4",
    // thumbnail: 'URL_TO_FORMULA_THUMBNAIL.jpg'
  },
  {
    id: 'portal-of-probabilities',
    title: "Bashar - Exploring the Portal of Probabilities (2019-05-04)",
    subtitle: "Journey into the infinite realm of probabilities and learn how to navigate and choose your desired reality.",
    url: "https://archive.org/download/bashar-exploring-the-portal-of-probabilities-2019-05-04_202406/Bashar%20-%20Exploring%20the%20Portal%20of%20Probabilities%20%282019-05-04%29.ia.mp4",
    // thumbnail: 'URL_TO_PORTAL_THUMBNAIL.jpg'
  },
  {
    id: 'shifting-realities-sample',
    title: "Bashar - Shifting Realities (Sample)",
    subtitle: "A glimpse into the concepts of shifting realities and conscious creation, available as a sample.",
    url: "https://archive.org/download/bashar-shifting-realities_202406/Bashar%20-%20Shifting%20Realities%20%282024-12-21%29.ia.mp4",
    // thumbnail: 'URL_TO_SHIFTING_THUMBNAIL.jpg'
  },
  {
    id: '1-3-5-7-11-refresher-course',
    title: "Bashar - 1-3-5-7-11 - A Refresher Course (2020-07-18)",
    subtitle: "A comprehensive review of Bashar's fundamental principles, offering fresh insights and deeper understanding.",
    url: "https://archive.org/download/bashar-1-3-5-7-11-a-refresher-course-2020-07-18_202406/Bashar%20-%201-3-5-7-11%20-%20A%20Refresher%20Course%20%282020-07-18%29.ia.mp4",
  },
  // Add more user-specific videos here
];

// Simulate an API call to fetch user's accessible transmissions
const fetchUserTransmissions = () => {
  return new Promise((resolve) => {
    // Simulate network delay
    setTimeout(() => {
      resolve(mockUserTransmissions);
    }, 1500);
  });
};

// --- VideoCard Component (reused/adapted) ---
// Encapsulates the logic for displaying a video thumbnail and playing the video on click.
const VideoCard = ({ video, index, onVideoPlay }) => {
  const { ref, inView } = useInView({
    threshold: 0.1, // Trigger when 10% of the element is visible
    triggerOnce: true, // Animate only once
  });

  const [showVideo, setShowVideo] = useState(false);

  const handlePlayClick = () => {
    setShowVideo(true);
    if (onVideoPlay) {
      onVideoPlay(video.title, video.url);
    }
  };

  return (
    <div
      ref={ref}
      className={`relative w-full max-w-sm mx-auto mb-8 // Adjusted max-width for grid layout
        rounded-xl overflow-hidden bg-black/30 border border-gray-700 shadow-lg shadow-gray-700/30
        ${inView ? "fade-in-animation" : "opacity-0"} 
      `}
      style={{ animationDelay: `${index * 150}ms` }}
    >
      <div className="p-4 sm:p-6 flex flex-col h-full"> {/* Ensure card takes full height for consistent look */}
        <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 line-clamp-2">{video.title}</h3>
        {video.subtitle && (
          <p className="text-xs sm:text-sm text-gray-400 mb-4 flex-grow line-clamp-3">{video.subtitle}</p>
        )}
        <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center mt-auto"> {/* mt-auto pushes video to bottom */}
          {showVideo ? (
            <video
              className="w-full h-full object-contain"
              controls
              controlsList="nodownload"
              autoPlay // Start playing automatically once loaded
              src={video.url}
            >
              Your browser does not support video playback.
            </video>
          ) : (
            <div
              className="w-full h-full bg-cover bg-center flex items-center justify-center cursor-pointer group"
              style={{ backgroundImage: `url(${video.thumbnail || defaultVideoThumb})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundColor: '#333' }}
              onClick={handlePlayClick}
              aria-label={`Play ${video.title}`}
            >
              {/* Optional: Overlay darker background for play button visibility if thumbnail is bright */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300"></div>
              <FaPlayCircle className="text-white text-6xl sm:text-8xl opacity-80 group-hover:opacity-100 transition-opacity duration-300 relative z-10" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main UserTransmissions Component ---
const UserTransmissions = () => {
  const location = useLocation();
  const [transmissions, setTransmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTransmissions = async () => {
      try {
        setLoading(true);
        const data = await fetchUserTransmissions();
        setTransmissions(data);
      } catch (err) {
        console.error("Failed to fetch user transmissions:", err);
        setError("Failed to load your transmissions. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadTransmissions();
  }, [location]);

  const handleVideoPlay = (title, url) => {
    sendCodedLog({
      message: `El usuario inició la reproducción del video: "${title}"`,
      event: 'user_transmission_video_play_en',
      videoTitle: title,
      videoUrl: url,
      ...Common.getCommonLogData(location)
    });
  };

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">
        {/* Page Title */}
        <h1 className="text-3xl sm:text-5xl font-bold text-center mb-4" style={{ fontFamily: '"Cinzel Decorative", serif' }}>
          Your Bashar Transmissions
        </h1>
        <p className="text-base sm:text-lg text-gray-400 text-center max-w-2xl mb-10">
          Here you'll find all the Bashar transmissions you have purchased or that are available as samples.
        </p>

        {loading && (
          <p className="text-xl text-gray-300">Loading transmissions...</p>
        )}

        {error && (
          <p className="text-xl text-red-500">{error}</p>
        )}

        {!loading && !error && transmissions.length === 0 && (
          <p className="text-xl text-gray-300">You haven't purchased any transmissions.</p>
        )}

        {/* Transmissions Grid */}
        {!loading && !error && transmissions.length > 0 && (
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {transmissions.map((video, index) => (
              <VideoCard
                key={video.id} // Use unique ID for key
                video={video}
                index={index}
                onVideoPlay={handleVideoPlay}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
      </footer>
    </div>
  );
};

export default UserTransmissions;